import React, { useState, useEffect, useMemo } from "react";
import gtaLogo from "./imgs/logotipo_v5.png";
import {
    Film,
    Tv,
    Plus,
    Search,
    Filter,
    Settings,
    X,
    Trash2,
    Edit,
    ChevronDown,
    Sparkles,
    Check,
    AlertCircle,
    FolderOpen,
    Eye,
    LogIn,
    LogOut,
    User,
} from "lucide-react";

// API Base URL
const API_BASE = process.env.REACT_APP_API_BASE || "/api";

// Constantes
const VIDEO_FORMATS = [
    "DVD5",
    "DVD9",
    "BDRip",
    "BDRemux",
    "WEB-DL",
    "WEBRip",
    "HDTV",
    "DVDRip",
    "HDRip",
    "4K UHD",
    "Outro",
];

const CHANNELS = [
    "Canal Panda",
    "RTP ZigZag",
    "Cartoon Network",
    "Disney Channel",
    "Nickelodeon",
    "SIC K",
    "Netflix",
    "HBO",
    "Amazon Prime",
    "Disney+",
    "Apple TV+",
    "Outro",
];

const LANGUAGES = [
    { code: "pt", flag: "🇵🇹", name: "Português" },
    { code: "pt-br", flag: "🇧🇷", name: "Português BR" },
    { code: "en", flag: "🇬🇧", name: "Inglês" },
    { code: "en-us", flag: "🇺🇸", name: "Inglês US" },
    { code: "es", flag: "🇪🇸", name: "Espanhol" },
    { code: "fr", flag: "🇫🇷", name: "Francês" },
    { code: "de", flag: "🇩🇪", name: "Alemão" },
    { code: "it", flag: "🇮🇹", name: "Italiano" },
    { code: "jp", flag: "🇯🇵", name: "Japonês" },
    { code: "kr", flag: "🇰🇷", name: "Coreano" },
    { code: "cn", flag: "🇨🇳", name: "Chinês" },
    { code: "ru", flag: "🇷🇺", name: "Russo" },
];

const SERIES_STATUS = [
    { value: "ongoing", label: "Em andamento", color: "#fbbf24" },
    { value: "finished", label: "Terminada", color: "#4ade80" },
    { value: "cancelled", label: "Cancelada", color: "#ef4444" },
    { value: "unknown", label: "Desconhecido", color: "#6b7280" },
];

function App() {
    // Authentication state
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem("authToken"));
    const [authLoading, setAuthLoading] = useState(true);

    // App state
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(false);

    // UI state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showAddEpisodeModal, setShowAddEpisodeModal] = useState(false);
    const [showEditEpisodeModal, setShowEditEpisodeModal] = useState(false);
    const [editEpisodeData, setEditEpisodeData] = useState(null);
    const [seriesLinks, setSeriesLinks] = useState({});
    const [linksLoading, setLinksLoading] = useState(false);
    const [selectedHost, setSelectedHost] = useState("");
    const [authMode, setAuthMode] = useState("login"); // 'login' or 'register'
    const [selectedSeries, setSelectedSeries] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterChannel, setFilterChannel] = useState("");
    const [filterFormat, setFilterFormat] = useState("");
    const [filterType, setFilterType] = useState("");
    const [toast, setToast] = useState(null);
    const [groqKey, setGroqKey] = useState("");
    const [tmdbKey, setTmdbKey] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [coverLoading, setCoverLoading] = useState(false);
    const isAdmin = user?.isAdmin ?? user?.username === "scala";

    // Auth forms
    const [authForm, setAuthForm] = useState({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    // Form state
    const [formData, setFormData] = useState({
        title: "",
        type: "serie",
        channel: "",
        poster: "",
        synopsis: "",
        status: "unknown",
        totalSeasons: 1,
        totalEpisodes: 0,
        audioLanguages: [],
        subtitleLanguages: [],
        gtaInsert: false,
        seasons: [],
    });

    // Form has unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Episode form state
    const [episodeForm, setEpisodeForm] = useState({
        seasonNumber: 1,
        episodeNumber: 1,
        endEpisodeNumber: 1,
        name: "",
        namePattern: "",
        owned: true,
        batchMode: false,
        links: [],
    });

    // API helper function
    const apiRequest = async (endpoint, options = {}) => {
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
            ...options,
        };

        const response = await fetch(`${API_BASE}${endpoint}`, config);

        if (!response.ok) {
            const error = await response
                .json()
                .catch(() => ({ error: "Network error" }));
            throw new Error(error.error || `HTTP ${response.status}`);
        }

        return response.json();
    };

    // Authentication functions
    const login = async (username, password) => {
        try {
            const data = await apiRequest("/auth/login", {
                method: "POST",
                body: JSON.stringify({ username, password }),
            });

            setToken(data.token);
            setUser(data.user);
            localStorage.setItem("authToken", data.token);
            setShowAuthModal(false);
            showToast("Login realizado com sucesso!");
        } catch (error) {
            showToast(error.message, "error");
            throw error;
        }
    };

    const register = async (username, email, password) => {
        try {
            if (!token || !isAdmin) {
                showToast("Apenas o administrador pode criar utilizadores", "error");
                return;
            }

            const data = await apiRequest("/auth/register", {
                method: "POST",
                body: JSON.stringify({ username, email, password }),
            });

            showToast("Utilizador criado com sucesso!");
            setShowAuthModal(false);
            setAuthMode("login");
            setAuthForm({
                username: "",
                email: "",
                password: "",
                confirmPassword: "",
            });
        } catch (error) {
            showToast(error.message, "error");
            throw error;
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        setSeries([]);
        localStorage.removeItem("authToken");
        showToast("Sessão terminada");
    };

    // Check authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            if (token) {
                try {
                    const data = await apiRequest("/auth/profile");
                    setUser(data.user);
                } catch (error) {
                    console.error("Auth check failed:", error);
                    logout();
                }
            }
            setAuthLoading(false);
        };

        checkAuth();
    }, [token]);

    // Load series when app starts (always, even without login)
    useEffect(() => {
        loadSeries();
    }, [user]);

    // Load series from API
    const loadSeries = async () => {
        try {
            setLoading(true);
            const data = await apiRequest("/series");
            setSeries(data.series);
        } catch (error) {
            console.error("Load series error:", error);
            showToast("Erro ao carregar séries", "error");
        } finally {
            setLoading(false);
        }
    };

    // Load API keys from localStorage (these are client-side only)
    useEffect(() => {
        setGroqKey(localStorage.getItem("groqKey") || "");
        setTmdbKey(localStorage.getItem("tmdbKey") || "");
    }, []);

    // Mostrar toast
    const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    // Filtrar séries
    const filteredSeries = useMemo(() => {
        return series.filter((s) => {
            const matchSearch = s.title
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            const matchChannel = !filterChannel || s.channel === filterChannel;
            const matchType = !filterType || s.type === filterType;

            let matchFormat = true;
            if (filterFormat) {
                matchFormat = s.seasons?.some((season) =>
                    season.episodes?.some((ep) => ep.format === filterFormat),
                );
            }

            return matchSearch && matchChannel && matchFormat && matchType;
        });
    }, [series, searchTerm, filterChannel, filterFormat, filterType]);

    const getSeriesTotalEpisodes = (serie) => {
        if (typeof serie.totalEpisodes === "number") return serie.totalEpisodes;
        if (Array.isArray(serie.seasons)) {
            return serie.seasons.reduce(
                (total, season) => total + (season.episodes?.length || 0),
                0,
            );
        }
        return 0;
    };

    const getSeriesOwnedEpisodes = (serie) => {
        if (typeof serie.episodesOwned === "number") return serie.episodesOwned;
        let owned = 0;
        serie.seasons?.forEach((season) => {
            owned += season.episodes?.filter((ep) => ep.owned).length || 0;
        });
        return owned;
    };

    // Calcular estatísticas (computed)
    const computedStats = useMemo(() => {
        const totalSeries = series.filter((s) => s.type === "serie").length;
        const totalFilmes = series.filter((s) => s.type === "filme").length;
        let totalEpisodes = 0;
        let ownedEpisodes = 0;

        series.forEach((s) => {
            if (s.type === "serie") {
                totalEpisodes += getSeriesTotalEpisodes(s);
                ownedEpisodes += getSeriesOwnedEpisodes(s);
            }
        });

        return { totalSeries, totalFilmes, totalEpisodes, ownedEpisodes };
    }, [series]);

    const seriesLinkHosts = useMemo(
        () => Object.keys(seriesLinks || {}),
        [seriesLinks],
    );

    const activeHostLinks = selectedHost && seriesLinks[selectedHost]
        ? seriesLinks[selectedHost]
        : [];

    // Calcular progresso de uma série
    const calculateProgress = (serie) => {
        if (serie.type === "filme") return serie.owned ? 100 : 0;

        const total = getSeriesTotalEpisodes(serie);
        const owned = getSeriesOwnedEpisodes(serie);

        return total > 0 ? Math.round((owned / total) * 100) : 0;
    };

    // Episódios em falta
    const getMissingEpisodes = (serie) => {
        if (serie.type === "filme") return [];

        const missing = [];
        const totalSeasons = serie.totalSeasons || 1;

        for (let s = 1; s <= totalSeasons; s++) {
            const season = serie.seasons?.find((sea) => sea.number === s);
            const seasonEpCount =
                serie.episodesPerSeason?.[s] ||
                Math.ceil((serie.totalEpisodes || 0) / totalSeasons);

            for (let e = 1; e <= seasonEpCount; e++) {
                const episode = season?.episodes?.find((ep) => ep.number === e);
                if (!episode?.owned) {
                    missing.push(
                        `S${String(s).padStart(2, "0")}E${String(e).padStart(2, "0")}`,
                    );
                }
            }
        }

        return missing;
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: "",
            type: "serie",
            channel: "",
            poster: "",
            synopsis: "",
            status: "unknown",
            totalSeasons: 1,
            totalEpisodes: 0,
            audioLanguages: [],
            subtitleLanguages: [],
            gtaInsert: false,
            genre: "",
            duration: "",
            containerFormat: "",
            videoInfo: "",
            audioInfo: "",
            subtitleInfo: "",
            seasons: [],
        });
        setEditMode(false);
    };

    // Abrir modal de adicionar
    const openAddModal = () => {
        resetForm();
        setHasUnsavedChanges(false);
        setShowAddModal(true);
    };

    // Abrir modal de editar
    const openEditModal = (serie) => {
        setFormData({ ...serie });
        setEditMode(true);
        setHasUnsavedChanges(false);
        setShowAddModal(true);
        setShowDetailModal(false);
    };

    // Abrir modal de detalhes
    const openDetailModal = async (serie) => {
        setSelectedSeries(serie);
        setSelectedHost("");
        setSeriesLinks({});
        setShowDetailModal(true);
        try {
            const data = await apiRequest(`/series/${serie.id}`);
            setSelectedSeries(data.series);
            loadSeriesLinks(serie.id, true);
        } catch (error) {
            console.error("Load series detail error:", error);
        }
    };

    // Verificar se há mudanças não guardadas
    const checkForUnsavedChanges = (newData) => {
        // Comparação simples - em produção poderia ser mais sofisticada
        const initialData = editMode
            ? selectedSeries
            : {
                  title: "",
                  type: "serie",
                  channel: "",
                  poster: "",
                  synopsis: "",
                  status: "unknown",
                  totalSeasons: 1,
                  totalEpisodes: 0,
                  audioLanguages: [],
                  subtitleLanguages: [],
                  gtaInsert: false,
                  seasons: [],
              };

        const hasChanges =
            JSON.stringify(newData) !== JSON.stringify(initialData);
        setHasUnsavedChanges(hasChanges);
    };

    // Guardar série
    const saveSeries = async () => {
        if (!formData.title.trim()) {
            showToast("Por favor, insere um título", "error");
            return;
        }

        try {
            if (editMode) {
                await apiRequest(`/series/${formData.id}`, {
                    method: "PUT",
                    body: JSON.stringify(formData),
                });
                setSeries((prev) =>
                    prev.map((s) => (s.id === formData.id ? formData : s)),
                );
                showToast("Série atualizada com sucesso!");
            } else {
                const data = await apiRequest("/series", {
                    method: "POST",
                    body: JSON.stringify(formData),
                });
                const newSerie = { ...formData, id: data.seriesId };
                setSeries((prev) => [...prev, newSerie]);
                showToast("Série adicionada com sucesso!");
            }

            // Reload data to get updated stats
            loadSeries();

            setShowAddModal(false);
            resetForm();
        } catch (error) {
            showToast("Erro ao guardar série: " + error.message, "error");
        }
    };

    // Guardar episódio
    const saveEpisode = async (andContinue = false) => {
        if (!episodeForm.seasonNumber || !episodeForm.episodeNumber) {
            showToast("Temporada e número de episódio são obrigatórios", "error");
            return;
        }

        if (!selectedSeries) {
            showToast("Nenhuma série selecionada", "error");
            return;
        }

        try {
            if (episodeForm.batchMode) {
                // Batch mode: add multiple episodes
                const startEp = episodeForm.episodeNumber;
                const endEp = episodeForm.endEpisodeNumber;

                if (endEp < startEp) {
                    showToast("Número final deve ser maior ou igual ao inicial", "error");
                    return;
                }

                let successCount = 0;
                let failCount = 0;

                for (let epNum = startEp; epNum <= endEp; epNum++) {
                    try {
                        let epName = episodeForm.namePattern
                            ? episodeForm.namePattern.replace(/\{n\}/g, epNum)
                            : `Episódio ${epNum}`;

                        const result = await apiRequest(`/series/${selectedSeries.id}/episodes`, {
                            method: "POST",
                            body: JSON.stringify({
                                seasonNumber: episodeForm.seasonNumber,
                                episodeNumber: epNum,
                                name: epName,
                                owned: episodeForm.owned,
                            }),
                        });

                        // Add links if provided
                        if (result.episodeId && episodeForm.links.length > 0) {
                            for (const link of episodeForm.links) {
                                if (link.server && link.url) {
                                    try {
                                        await apiRequest(`/series/episodes/${result.episodeId}/links`, {
                                            method: "POST",
                                            body: JSON.stringify({ name: link.server, url: link.url }),
                                        });
                                    } catch (e) { /* link add failed */ }
                                }
                            }
                        }
                        successCount++;
                    } catch (error) {
                        failCount++;
                    }
                }

                if (successCount > 0) {
                    showToast(`${successCount} episódio(s) adicionado(s) com sucesso!`);
                }
                if (failCount > 0) {
                    showToast(`${failCount} episódio(s) falharam (podem já existir)`, "error");
                }
            } else {
                // Single episode mode
                const result = await apiRequest(`/series/${selectedSeries.id}/episodes`, {
                    method: "POST",
                    body: JSON.stringify({
                        seasonNumber: episodeForm.seasonNumber,
                        episodeNumber: episodeForm.episodeNumber,
                        name: episodeForm.name,
                        owned: episodeForm.owned,
                    }),
                });

                // Add links if provided
                if (result.episodeId && episodeForm.links.length > 0) {
                    for (const link of episodeForm.links) {
                        if (link.server && link.url) {
                            try {
                                await apiRequest(`/series/episodes/${result.episodeId}/links`, {
                                    method: "POST",
                                    body: JSON.stringify({ name: link.server, url: link.url }),
                                });
                            } catch (e) { /* link add failed, episode still created */ }
                        }
                    }
                }

                showToast("Episódio adicionado com sucesso!");
            }

            // Reload series detail to show new episode(s)
            const data = await apiRequest(`/series/${selectedSeries.id}`);
            setSelectedSeries(data.series);
            loadSeriesLinks(data.series.id, true);

            // Reload list
            loadSeries();

            if (andContinue) {
                // Reset form for next episode, keep season and increment episode number
                const nextEp = episodeForm.batchMode
                    ? episodeForm.endEpisodeNumber + 1
                    : episodeForm.episodeNumber + 1;
                setEpisodeForm((prev) => ({
                    ...prev,
                    episodeNumber: nextEp,
                    endEpisodeNumber: nextEp,
                    name: "",
                    namePattern: "",
                    links: prev.links.map((l) => ({ ...l, url: "" })),
                }));
            } else {
                setShowAddEpisodeModal(false);
            }
        } catch (error) {
            showToast("Erro ao adicionar episódio: " + error.message, "error");
        }
    };

    // Open edit episode modal
    const openEditEpisode = (ep, seasonNumber) => {
        setEditEpisodeData({
            id: ep.id,
            seasonNumber,
            number: ep.number,
            name: ep.name || "",
            owned: ep.owned,
            links: (ep.links || []).filter(l => l && l.id).map((l) => ({
                id: l.id,
                server: l.name,
                url: l.url,
            })),
        });
        setShowEditEpisodeModal(true);
    };

    const addEditEpisodeLink = () => {
        setEditEpisodeData((prev) => ({
            ...prev,
            links: [...(prev.links || []), { server: "Mega", url: "" }],
        }));
    };

    const updateEditEpisodeLink = (index, field, value) => {
        setEditEpisodeData((prev) => {
            const updated = [...(prev.links || [])];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, links: updated };
        });
    };

    const removeEditEpisodeLink = (index) => {
        setEditEpisodeData((prev) => ({
            ...prev,
            links: (prev.links || []).filter((_, i) => i !== index),
        }));
    };

    // Save edited episode
    const saveEditedEpisode = async () => {
        if (!editEpisodeData) return;
        try {
            await apiRequest(`/series/episodes/${editEpisodeData.id}`, {
                method: "PUT",
                body: JSON.stringify({
                    name: editEpisodeData.name,
                    owned: editEpisodeData.owned,
                    links: editEpisodeData.links,
                }),
            });
            showToast("Episódio atualizado!");
            setShowEditEpisodeModal(false);
            // Reload detail
            const data = await apiRequest(`/series/${selectedSeries.id}`);
            setSelectedSeries(data.series);
            loadSeriesLinks(data.series.id, true);
            loadSeries();
        } catch (error) {
            showToast("Erro ao atualizar episódio: " + error.message, "error");
        }
    };

    // Delete single episode
    const deleteEpisode = async (episodeId) => {
        if (!window.confirm("Apagar este episódio?")) return;
        try {
            await apiRequest(`/series/episodes/${episodeId}`, { method: "DELETE" });
            showToast("Episódio apagado!");
            setShowEditEpisodeModal(false);
            const data = await apiRequest(`/series/${selectedSeries.id}`);
            setSelectedSeries(data.series);
            loadSeriesLinks(data.series.id, true);
            loadSeries();
        } catch (error) {
            showToast("Erro ao apagar episódio: " + error.message, "error");
        }
    };

    // Apagar série
    const deleteSeries = async (id) => {
        if (window.confirm("Tens a certeza que queres apagar esta série?")) {
            try {
                await apiRequest(`/series/${id}`, { method: "DELETE" });
                setSeries((prev) => prev.filter((s) => s.id !== id));
                setShowDetailModal(false);
                // Stats update comes from series list
                showToast("Série apagada!");
            } catch (error) {
                showToast("Erro ao apagar série: " + error.message, "error");
            }
        }
    };

    const handleGtaChange = async (seriesId, value) => {
        try {
            const gtaInsert = value === "null" ? null : value === "sim";

            await apiRequest(`/series/${seriesId}/gta`, {
                method: "PATCH",
                body: JSON.stringify({ gtaInsert }),
            });

            // Update local state
            setSeries((prev) =>
                prev.map((s) =>
                    s.id === seriesId ? { ...s, gtaInsert } : s
                )
            );

            // Update selected series if it's the one being modified
            setSelectedSeries((prev) =>
                prev && prev.id === seriesId
                    ? { ...prev, gtaInsert }
                    : prev
            );

            showToast("Estado GTA atualizado!");
        } catch (error) {
            showToast("Erro ao atualizar estado GTA: " + error.message, "error");
        }
    };

    // Delete link
    const deleteEpisodeLink = async (linkId) => {
        try {
            await apiRequest(`/series/links/${linkId}`, { method: "DELETE" });
            showToast("Link removido!");
            const data = await apiRequest(`/series/${selectedSeries.id}`);
            setSelectedSeries(data.series);
            loadSeriesLinks(data.series.id, true);
        } catch (error) {
            showToast("Erro ao remover link: " + error.message, "error");
        }
    };

    // Load all links grouped by host
    const loadSeriesLinks = async (seriesId, autoSelect = false) => {
        try {
            setLinksLoading(true);
            const data = await apiRequest(`/series/${seriesId}/links`);
            const links = data.links || {};
            setSeriesLinks(links);
            if (Object.keys(links).length === 0) {
                setSelectedHost("");
            } else if (autoSelect) {
                setSelectedHost((prev) =>
                    prev && links[prev] ? prev : Object.keys(links)[0],
                );
            }
        } catch (error) {
            showToast("Erro ao carregar links: " + error.message, "error");
        } finally {
            setLinksLoading(false);
        }
    };

    // Server logo URLs
    const getServerLogo = (host) => {
        const h = host.toLowerCase();
        if (h.includes("mega")) return "https://mega.nz/favicon.ico";
        if (h.includes("1fichier")) return "https://1fichier.com/favicon.ico";
        if (h.includes("google") || h.includes("gdrive")) return "https://drive.google.com/favicon.ico";
        if (h.includes("mediafire")) return "https://www.mediafire.com/favicon.ico";
        if (h.includes("dropbox")) return "https://www.dropbox.com/static/images/favicon.ico";
        if (h.includes("onedrive")) return "https://onedrive.live.com/favicon.ico";
        if (h.includes("uptobox")) return "https://uptobox.com/favicon.ico";
        return null;
    };

    // Copy all links for a server to clipboard
    const copyServerLinks = (host, links) => {
        const text = links.map((l) =>
            `T${String(l.seasonNumber).padStart(2, "0")} E${String(l.episodeNumber).padStart(2, "0")} - ${l.episodeName || "Sem nome"}\n${l.url}`
        ).join("\n\n");
        navigator.clipboard.writeText(text).then(() => {
            showToast(`${links.length} links de ${host} copiados!`);
        }).catch(() => {
            showToast("Erro ao copiar", "error");
        });
    };

    // Copy only URLs for a server
    const copyServerUrls = (host, links) => {
        const text = links.map((l) => l.url).join("\n");
        navigator.clipboard.writeText(text).then(() => {
            showToast(`${links.length} URLs de ${host} copiados!`);
        }).catch(() => {
            showToast("Erro ao copiar", "error");
        });
    };

    // Parse MediaInfo text and fill form fields
    const parseMediaInfo = (text) => {
        if (!text.trim()) return;

        // Split into sections (General, Video, Audio, Text/Subtitle)
        const sections = {};
        let currentSection = "General";
        for (const line of text.split("\n")) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            // Section headers are single words or short phrases without ":"
            if (
                !trimmed.includes(":") &&
                /^(General|Video|Audio|Text|Menu|Image|Other)/.test(trimmed)
            ) {
                currentSection = trimmed.split(/\s/)[0];
                if (!sections[currentSection]) sections[currentSection] = {};
                continue;
            }
            const colonIdx = trimmed.indexOf(":");
            if (colonIdx > 0) {
                const key = trimmed.substring(0, colonIdx).trim();
                const val = trimmed.substring(colonIdx + 1).trim();
                if (!sections[currentSection]) sections[currentSection] = {};
                // Keep first occurrence for most, but for Audio allow multiple
                if (!sections[currentSection][key]) {
                    sections[currentSection][key] = val;
                }
            }
        }

        const gen = sections["General"] || {};
        const vid = sections["Video"] || {};
        const aud = sections["Audio"] || {};
        const sub = sections["Text"] || {};

        const updates = {};

        // Container format
        if (gen["Format"]) {
            const fmtMap = { Matroska: ".mkv", "MPEG-4": ".mp4", AVI: ".avi", "BDAV": ".m2ts", "Flash Video": ".flv" };
            updates.containerFormat = fmtMap[gen["Format"]] || gen["Format"];
        }

        // Duration
        if (gen["Duration"] || vid["Duration"]) {
            const raw = gen["Duration"] || vid["Duration"];
            // Parse "23 min 40 s" or "1 h 23 min" or "23mn 40s" etc.
            const hMatch = raw.match(/(\d+)\s*h/);
            const mMatch = raw.match(/(\d+)\s*m/);
            const sMatch = raw.match(/(\d+)\s*s/);
            let parts = [];
            if (hMatch) parts.push(`${hMatch[1]}h`);
            if (mMatch) parts.push(`${mMatch[1]}m`);
            if (sMatch) parts.push(`${sMatch[1]}s`);
            if (parts.length > 0) {
                updates.duration = `±${parts.join("")}`;
            }
        }

        // Video info: codec | fps | resolution (aspect)
        if (Object.keys(vid).length > 0) {
            const parts = [];
            // Codec
            const codec = vid["Format"] || "";
            const codecProfile = vid["Format profile"] || "";
            const codecId = vid["Codec ID"] || "";
            if (codec === "AVC" || codecId.includes("AVC") || codecId.includes("avc")) {
                parts.push("x264");
            } else if (codec === "HEVC" || codecId.includes("HEVC") || codecId.includes("hevc")) {
                parts.push("x265");
            } else if (codec) {
                parts.push(codec);
            }

            // Bit depth
            const bitDepth = vid["Bit depth"];
            if (bitDepth && !bitDepth.includes("8")) {
                parts.push(bitDepth.replace(/\s*bits?/i, "").trim() + "bit");
            }

            // Frame rate
            const fps = vid["Frame rate"] || vid["Original frame rate"];
            if (fps) {
                const fpsVal = fps.match(/([\d.]+)/);
                if (fpsVal) parts.push(`${fpsVal[1]}fps`);
            }

            // Resolution
            const width = vid["Width"];
            const height = vid["Height"];
            if (width && height) {
                const w = width.replace(/[^\d]/g, "");
                const h = height.replace(/[^\d]/g, "");
                const aspect = vid["Display aspect ratio"] || "";
                let res = `${w}x${h}`;
                if (aspect) res += ` (${aspect})`;
                parts.push(res);
            }

            if (parts.length > 0) {
                updates.videoInfo = parts.join(" | ");
            }
        }

        // Audio info: codec (channels) bitrate [language]
        if (Object.keys(aud).length > 0) {
            const parts = [];
            const codec = aud["Format"] || "";
            const profile = aud["Format profile"] || "";
            let audioCodec = codec;
            if (codec === "AAC" && profile) {
                audioCodec = `${codec}-${profile}`;
            } else if (codec === "AC-3" || codec === "E-AC-3") {
                audioCodec = codec;
            }
            parts.push(audioCodec);

            // Channels
            const channels = aud["Channel(s)"];
            if (channels) {
                const chNum = channels.replace(/[^\d]/g, "");
                const chMap = { "1": "1.0", "2": "2.0", "6": "5.1", "8": "7.1" };
                parts[0] += ` (${chMap[chNum] || chNum + "ch"})`;
            }

            // Bitrate
            const bitrate = aud["Bit rate"] || aud["Overall bit rate"];
            if (bitrate) {
                const brMatch = bitrate.match(/([\d.]+)\s*(kb|mb)/i);
                if (brMatch) {
                    parts.push(`${brMatch[1]}${brMatch[2].toLowerCase()}ps`);
                }
            }

            // Language
            const lang = aud["Language"];
            if (lang) {
                parts.push(lang);
            }

            if (parts.length > 0) {
                updates.audioInfo = parts.join(" ");
            }
        }

        // Subtitles
        if (Object.keys(sub).length > 0) {
            const parts = [];
            const fmt = sub["Format"] || "";
            const lang = sub["Language"] || "";
            const title = sub["Title"] || "";
            if (fmt) {
                const fmtLower = fmt.toLowerCase();
                if (fmtLower.includes("utf-8") || fmtLower.includes("ass") || fmtLower.includes("ssa")) {
                    parts.push(fmtLower.includes("ass") || fmtLower.includes("ssa") ? ".ass" : ".srt");
                } else if (fmtLower.includes("pgs")) {
                    parts.push("PGS");
                } else if (fmtLower.includes("vobsub")) {
                    parts.push("VobSub");
                } else {
                    parts.push(fmt);
                }
                parts.push("softsub");
            }
            if (lang) parts.push(`(${lang})`);
            updates.subtitleInfo = parts.join(" ") || "N/A";
        }

        // Apply updates to form
        if (Object.keys(updates).length > 0) {
            setFormData((prev) => ({ ...prev, ...updates }));
            showToast("Campos preenchidos com dados do MediaInfo!");
        } else {
            showToast("Não foi possível extrair dados do texto colado.", "error");
        }
    };

    // Toggle idioma
    const toggleLanguage = (code, field) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].includes(code)
                ? prev[field].filter((c) => c !== code)
                : [...prev[field], code],
        }));
    };

    // Adicionar temporada
    const addSeason = () => {
        const newSeasonNumber = (formData.seasons?.length || 0) + 1;
        setFormData((prev) => ({
            ...prev,
            totalSeasons: newSeasonNumber,
            seasons: [
                ...(prev.seasons || []),
                { number: newSeasonNumber, episodes: [] },
            ],
        }));
    };

    // Adicionar episódio
    const addEpisode = (seasonNumber) => {
        setFormData((prev) => ({
            ...prev,
            seasons: prev.seasons.map((season) => {
                if (season.number === seasonNumber) {
                    const newEpNumber = (season.episodes?.length || 0) + 1;
                    return {
                        ...season,
                        episodes: [
                            ...(season.episodes || []),
                            {
                                number: newEpNumber,
                                name: "",
                                format: "WEB-DL",
                                audioQuality: "",
                                videoQuality: "",
                                fileSize: 0,
                                links: [],
                                owned: true,
                            },
                        ],
                    };
                }
                return season;
            }),
        }));
    };

    // Atualizar episódio
    const updateEpisode = (seasonNumber, epNumber, field, value) => {
        setFormData((prev) => ({
            ...prev,
            seasons: prev.seasons.map((season) => {
                if (season.number === seasonNumber) {
                    return {
                        ...season,
                        episodes: season.episodes.map((ep) => {
                            if (ep.number === epNumber) {
                                return { ...ep, [field]: value };
                            }
                            return ep;
                        }),
                    };
                }
                return season;
            }),
        }));
    };

    // Remover episódio
    const removeEpisode = (seasonNumber, epNumber) => {
        setFormData((prev) => ({
            ...prev,
            seasons: prev.seasons.map((season) => {
                if (season.number === seasonNumber) {
                    return {
                        ...season,
                        episodes: season.episodes.filter(
                            (ep) => ep.number !== epNumber,
                        ),
                    };
                }
                return season;
            }),
        }));
    };

    // Extrair metadados do vídeo
    const extractVideoMetadata = (file) => {
        return new Promise((resolve) => {
            const video = document.createElement("video");
            const url = URL.createObjectURL(file);

            video.preload = "metadata";
            video.src = url;

            video.onloadedmetadata = () => {
                URL.revokeObjectURL(url);
                resolve({
                    duration: video.duration,
                    videoWidth: video.videoWidth,
                    videoHeight: video.videoHeight,
                    fileSize: file.size,
                });
            };

            video.onerror = () => {
                URL.revokeObjectURL(url);
                resolve({
                    fileSize: file.size,
                });
            };
        });
    };

    // Processar upload de vídeo
    const handleVideoUpload = async (seasonNumber, epNumber, file) => {
        try {
            const metadata = await extractVideoMetadata(file);

            // Auto-preencher campos baseados no ficheiro
            const updates = {
                fileSize: metadata.fileSize,
            };

            // Tentar extrair qualidade do vídeo
            if (metadata.videoHeight) {
                if (metadata.videoHeight >= 2160) {
                    updates.videoQuality = "4K UHD";
                } else if (metadata.videoHeight >= 1080) {
                    updates.videoQuality = "1080p Full HD";
                } else if (metadata.videoHeight >= 720) {
                    updates.videoQuality = "720p HD";
                } else {
                    updates.videoQuality = `${metadata.videoWidth}x${metadata.videoHeight}`;
                }
            }

            // Atualizar episódio com metadados
            Object.entries(updates).forEach(([field, value]) => {
                updateEpisode(seasonNumber, epNumber, field, value);
            });

            showToast("Metadados extraídos com sucesso!");
        } catch (error) {
            console.error("Erro ao processar vídeo:", error);
            showToast("Erro ao processar o ficheiro de vídeo", "error");
        }
    };

    // Adicionar link
    const addLink = (seasonNumber, epNumber) => {
        const linkName = prompt("Nome do link (ex: Mega, Drive, etc.):");
        const linkUrl = prompt("URL do link:");

        if (linkName && linkUrl) {
            updateEpisode(seasonNumber, epNumber, "links", [
                ...(formData.seasons
                    .find((s) => s.number === seasonNumber)
                    ?.episodes.find((e) => e.number === epNumber)?.links || []),
                { name: linkName.trim(), url: linkUrl.trim() },
            ]);
            showToast("Link adicionado!");
        }
    };

    // Remover link
    const removeLink = (seasonNumber, epNumber, linkIndex) => {
        const episode = formData.seasons
            .find((s) => s.number === seasonNumber)
            ?.episodes.find((e) => e.number === epNumber);

        if (episode) {
            const newLinks = episode.links.filter((_, i) => i !== linkIndex);
            updateEpisode(seasonNumber, epNumber, "links", newLinks);
            showToast("Link removido!");
        }
    };

    // AI - Buscar informação com Groq
    const fetchAIInfo = async (type) => {
        if (!groqKey) {
            showToast(
                "Configura a tua API Key do Groq nas definições",
                "error",
            );
            setShowSettingsModal(true);
            return;
        }

        if (!formData.title) {
            showToast("Insere primeiro o título da série/filme", "error");
            return;
        }

        setAiLoading(true);

        try {
            let prompt = "";

            if (type === "all") {
                prompt = `Sobre a série/filme "${formData.title}", fornece em formato JSON:
{
  "synopsis": "sinopse curta em português (máximo 200 caracteres)",
  "totalSeasons": número de temporadas (0 se for filme),
  "totalEpisodes": número total de episódios (1 se for filme),
  "poster": "URL de uma imagem de poster/capa oficial se conheceres"
}
Responde APENAS com o JSON, sem texto adicional.`;
            } else if (type === "synopsis") {
                prompt = `Escreve uma sinopse curta em português (máximo 200 caracteres) para "${formData.title}". Responde apenas com a sinopse, sem aspas.`;
            } else if (type === "episodes") {
                prompt = `Quantas temporadas e episódios tem "${formData.title}"? Responde em JSON: {"totalSeasons": X, "totalEpisodes": Y}`;
            }

            const response = await fetch(
                "https://api.groq.com/openai/v1/chat/completions",
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${groqKey}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        model: "llama-3.3-70b-versatile",
                        messages: [{ role: "user", content: prompt }],
                        temperature: 0.3,
                        max_tokens: 500,
                    }),
                },
            );

            if (!response.ok) {
                throw new Error("Erro na API do Groq");
            }

            const data = await response.json();
            const content = data.choices[0]?.message?.content;

            if (type === "all") {
                try {
                    const info = JSON.parse(content);
                    setFormData((prev) => ({
                        ...prev,
                        synopsis: info.synopsis || prev.synopsis,
                        totalSeasons: info.totalSeasons || prev.totalSeasons,
                        totalEpisodes: info.totalEpisodes || prev.totalEpisodes,
                        poster: info.poster || prev.poster,
                    }));
                    showToast("Informação obtida com sucesso!");
                } catch {
                    showToast("Erro ao processar resposta da AI", "error");
                }
            } else if (type === "synopsis") {
                setFormData((prev) => ({ ...prev, synopsis: content.trim() }));
                showToast("Sinopse gerada!");
            } else if (type === "episodes") {
                try {
                    const info = JSON.parse(content);
                    setFormData((prev) => ({
                        ...prev,
                        totalSeasons: info.totalSeasons || prev.totalSeasons,
                        totalEpisodes: info.totalEpisodes || prev.totalEpisodes,
                    }));
                    showToast("Informação de episódios obtida!");
                } catch {
                    showToast("Erro ao processar resposta", "error");
                }
            }
        } catch (error) {
            console.error(error);
            showToast(
                "Erro ao contactar a AI. Verifica a tua API key.",
                "error",
            );
        }

        setAiLoading(false);
    };

    // Buscar capas no TMDB
    const searchTMDBCovers = async () => {
        if (!tmdbKey) {
            showToast(
                "Configura a tua API Key do TMDB nas definições",
                "error",
            );
            setShowSettingsModal(true);
            return;
        }

        if (!formData.title) {
            showToast("Insere primeiro o título", "error");
            return;
        }

        setCoverLoading(true);

        try {
            // Primeiro buscar por séries/filmes
            const searchType = formData.type === "serie" ? "tv" : "movie";
            const searchUrl = `https://api.themoviedb.org/3/search/${searchType}?api_key=${tmdbKey}&query=${encodeURIComponent(formData.title)}&language=pt-BR&page=1`;

            const searchResponse = await fetch(searchUrl);
            if (!searchResponse.ok) {
                throw new Error("Erro na API do TMDB");
            }

            const searchData = await searchResponse.json();

            if (!searchData.results || searchData.results.length === 0) {
                showToast("Nenhum resultado encontrado", "error");
                return;
            }

            // Pegar o primeiro resultado
            const item = searchData.results[0];
            const itemId = item.id;

            // Buscar imagens (posters)
            const imagesUrl = `https://api.themoviedb.org/3/${searchType}/${itemId}/images?api_key=${tmdbKey}`;
            const imagesResponse = await fetch(imagesUrl);

            if (!imagesResponse.ok) {
                throw new Error("Erro ao buscar imagens");
            }

            const imagesData = await imagesResponse.json();

            // Filtrar apenas posters em português ou inglês
            const posters =
                imagesData.posters
                    ?.filter(
                        (poster) =>
                            poster.iso_639_1 === "pt" ||
                            poster.iso_639_1 === "en" ||
                            !poster.iso_639_1,
                    )
                    .slice(0, 10) || [];

            if (posters.length === 0) {
                showToast("Nenhuma capa encontrada", "error");
                return;
            }

            // Mostrar modal de seleção de capa
            const selectedPoster = await new Promise((resolve) => {
                const posterUrls = posters.map(
                    (poster) =>
                        `https://image.tmdb.org/t/p/w500${poster.file_path}`,
                );

                // Criar modal temporário para seleção
                const modal = document.createElement("div");
                modal.style.cssText = `
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.8); z-index: 10000;
                    display: flex; align-items: center; justify-content: center;
                `;

                const modalContent = document.createElement("div");
                modalContent.style.cssText = `
                    background: var(--bg-card); padding: 20px; border-radius: 8px;
                    max-width: 600px; max-height: 80vh; overflow-y: auto;
                `;

                modalContent.innerHTML = `
                    <h3 style="margin-bottom: 16px; color: var(--text-primary);">Seleciona uma capa</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 12px;">
                        ${posterUrls
                            .map(
                                (url, index) => `
                            <img src="${url}" style="width: 100%; border-radius: 4px; cursor: pointer; transition: opacity 0.2s;"
                                 onclick="window.selectPoster('${url}')" />
                        `,
                            )
                            .join("")}
                    </div>
                    <button style="margin-top: 16px; padding: 8px 16px; background: var(--accent); color: white; border: none; border-radius: 4px; cursor: pointer;"
                            onclick="window.cancelPosterSelection()">Cancelar</button>
                `;

                modal.appendChild(modalContent);
                document.body.appendChild(modal);

                window.selectPoster = (url) => {
                    document.body.removeChild(modal);
                    resolve(url);
                };

                window.cancelPosterSelection = () => {
                    document.body.removeChild(modal);
                    resolve(null);
                };
            });

            if (selectedPoster) {
                setFormData((prev) => ({ ...prev, poster: selectedPoster }));
                showToast("Capa selecionada!");
            }
        } catch (error) {
            console.error("Erro ao buscar capas:", error);
            showToast("Erro ao buscar capas no TMDB", "error");
        }

        setCoverLoading(false);
    };

    // Renderizar bandeiras
    const renderFlags = (codes, type) => {
        return codes.map((code) => {
            const lang = LANGUAGES.find((l) => l.code === code);
            return lang ? (
                <span key={code} className="flag" title={lang.name}>
                    {lang.flag}
                </span>
            ) : null;
        });
    };

    return (
        <div className="app">
            {/* Header */}
            <header className="header">
                <h1>
                    <Film size={32} />
                    Video Tracker
                </h1>
                <div className="header-actions">
                    {user ? (
                        <>
                            <span
                                style={{
                                    marginRight: "12px",
                                    color: "var(--text-secondary)",
                                }}
                            >
                                <User
                                    size={16}
                                    style={{ marginRight: "4px" }}
                                />
                                {user.username}
                            </span>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowSettingsModal(true)}
                            >
                                <Settings size={18} />
                                Definições
                            </button>
                            {isAdmin && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setAuthMode("register");
                                        setAuthForm({
                                            username: "",
                                            email: "",
                                            password: "",
                                            confirmPassword: "",
                                        });
                                        setShowAuthModal(true);
                                    }}
                                >
                                    <User size={18} />
                                    Criar Utilizador
                                </button>
                            )}
                            <button
                                className="btn btn-primary"
                                onClick={openAddModal}
                            >
                                <Plus size={18} />
                                Adicionar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={logout}
                            >
                                <LogOut size={18} />
                                Sair
                            </button>
                        </>
                    ) : (
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setAuthMode("login");
                                setShowAuthModal(true);
                            }}
                        >
                            <LogIn size={18} />
                            Entrar
                        </button>
                    )}
                </div>
            </header>

            {/* Stats */}
            <div className="stats-bar">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Tv size={20} />
                    </div>
                    <div className="stat-info">
                        <h3>{computedStats.totalSeries}</h3>
                        <p>Séries</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Film size={20} />
                    </div>
                    <div className="stat-info">
                        <h3>{computedStats.totalFilmes}</h3>
                        <p>Filmes</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <FolderOpen size={20} />
                    </div>
                    <div className="stat-info">
                        <h3>
                            {computedStats.ownedEpisodes}/{computedStats.totalEpisodes}
                        </h3>
                        <p>Episódios</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="search-row">
                    <div className="search-wrapper">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Pesquisar séries e filmes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="filters-row">
                    <select
                        className="filter-select"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="">Todos os tipos</option>
                        <option value="serie">Séries</option>
                        <option value="filme">Filmes</option>
                    </select>
                    <select
                        className="filter-select"
                        value={filterChannel}
                        onChange={(e) => setFilterChannel(e.target.value)}
                    >
                        <option value="">Todos os canais</option>
                        {CHANNELS.map((ch) => (
                            <option key={ch} value={ch}>
                                {ch}
                            </option>
                        ))}
                    </select>
                    <select
                        className="filter-select"
                        value={filterFormat}
                        onChange={(e) => setFilterFormat(e.target.value)}
                    >
                        <option value="">Todos os formatos</option>
                        {VIDEO_FORMATS.map((f) => (
                            <option key={f} value={f}>
                                {f}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Grid */}
            {filteredSeries.length === 0 ? (
                <div className="empty-state">
                    <Film size={80} />
                    <h3>Nenhuma série ou filme encontrado</h3>
                    <p>
                        {user
                            ? "Começa por adicionar a tua primeira série ou filme à coleção"
                            : "Faz login para adicionar séries e filmes à tua coleção"}
                    </p>
                    {user && (
                        <button className="btn btn-primary" onClick={openAddModal}>
                            <Plus size={18} />
                            Adicionar primeiro
                        </button>
                    )}
                </div>
            ) : (
                <div className="series-grid">
                    {filteredSeries.map((serie) => {
                        const progress = calculateProgress(serie);
                        const owned = getSeriesOwnedEpisodes(serie);
                        const totalEpisodes = getSeriesTotalEpisodes(serie);

                        return (
                            <div
                                key={serie.id}
                                className="series-card"
                                onClick={() => openDetailModal(serie)}
                                style={{
                                    border:
                                        serie.gtaInsert === true
                                            ? "3px solid #FFD700"
                                            : serie.gtaInsert === false
                                            ? "3px solid #DC143C"
                                            : undefined,
                                    boxShadow:
                                        serie.gtaInsert === true
                                            ? "0 0 15px rgba(255, 215, 0, 0.5)"
                                            : serie.gtaInsert === false
                                            ? "0 0 15px rgba(220, 20, 60, 0.5)"
                                            : undefined,
                                }}
                            >
                                <div className="card-poster">
                                    {serie.poster ? (
                                        <img
                                            src={serie.poster}
                                            alt={serie.title}
                                        />
                                    ) : (
                                        <div className="card-poster-placeholder">
                                            {serie.type === "filme" ? (
                                                <Film size={48} />
                                            ) : (
                                                <Tv size={48} />
                                            )}
                                            <span>Sem poster</span>
                                        </div>
                                    )}
                                    {serie.channel && (
                                        <span className="card-channel">
                                            {serie.channel}
                                        </span>
                                    )}
                                    <span className="card-type">
                                        {serie.type === "filme"
                                            ? "Filme"
                                            : "Série"}
                                    </span>
                                </div>
                                <div className="card-content">
                                    <h3 className="card-title">
                                        {serie.title}
                                    </h3>
                                    {serie.gtaInsert === true && (
                                        <div
                                            style={{
                                                background: "linear-gradient(135deg, #FFD700, #FFA500)",
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginBottom: "6px",
                                            }}
                                        >
                                            <img
                                                src={gtaLogo}
                                                alt="GTA"
                                                style={{
                                                    height: "16px",
                                                    width: "auto",
                                                    display: "block",
                                                }}
                                            />
                                        </div>
                                    )}
                                    {serie.gtaInsert === false && (
                                        <div
                                            style={{
                                                background: "#DC143C",
                                                color: "#fff",
                                                padding: "3px 8px",
                                                borderRadius: "4px",
                                                fontSize: "0.7rem",
                                                fontWeight: "bold",
                                                display: "inline-block",
                                                marginBottom: "6px",
                                            }}
                                        >
                                            ✗ Não GTA
                                        </div>
                                    )}
                                    <div className="card-meta">
                                        {serie.type === "serie" && (
                                            <>
                                                <span>
                                                    {serie.totalSeasons || 0}{" "}
                                                    temp.
                                                </span>
                                                <span>
                                                    {getSeriesTotalEpisodes(serie)}{" "}
                                                    eps.
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    {serie.type === "serie" && (
                                        <div className="card-progress">
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{
                                                        width: `${progress}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="progress-text">
                                                {owned} de{" "}
                                                {totalEpisodes}{" "}
                                                episódios ({progress}%)
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal Adicionar/Editar */}
            {showAddModal && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        if (hasUnsavedChanges) {
                            if (
                                window.confirm(
                                    "Tens mudanças não guardadas. Queres sair mesmo assim?",
                                )
                            ) {
                                setShowAddModal(false);
                            }
                        } else {
                            setShowAddModal(false);
                        }
                    }}
                >
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                {editMode ? "Editar" : "Adicionar Nova"}{" "}
                                Série/Filme
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowAddModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {authMode === "register" && (
                                <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: "12px" }}>
                                    Apenas o administrador pode criar utilizadores.
                                </p>
                            )}
                            {/* Info básica */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        Título *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Nome da série ou filme"
                                        value={formData.title}
                                        onChange={(e) => {
                                            const newData = {
                                                ...formData,
                                                title: e.target.value,
                                            };
                                            setFormData(newData);
                                            checkForUnsavedChanges(newData);
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Tipo</label>
                                    <select
                                        className="form-select"
                                        value={formData.type}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                type: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="serie">Série</option>
                                        <option value="filme">Filme</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        Canal/Plataforma
                                    </label>
                                    <select
                                        className="form-select"
                                        value={formData.channel}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                channel: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Selecionar...</option>
                                        {CHANNELS.map((ch) => (
                                            <option key={ch} value={ch}>
                                                {ch}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">
                                        URL do Poster
                                    </label>
                                    <div
                                        style={{ display: "flex", gap: "8px" }}
                                    >
                                        <input
                                            type="url"
                                            className="form-input"
                                            placeholder="https://..."
                                            value={formData.poster}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    poster: e.target.value,
                                                }))
                                            }
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            className="btn btn-secondary btn-small"
                                            onClick={searchTMDBCovers}
                                            disabled={coverLoading}
                                            title="Buscar capa no TMDB"
                                            style={{
                                                padding: "8px",
                                                minWidth: "40px",
                                            }}
                                        >
                                            {coverLoading ? (
                                                <div className="spinner" />
                                            ) : (
                                                "🎬"
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Sinopse</label>
                                <textarea
                                    className="form-textarea"
                                    placeholder="Descrição da série ou filme..."
                                    value={formData.synopsis}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            synopsis: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Estado da Série
                                </label>
                                <select
                                    className="form-select"
                                    value={formData.status}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            status: e.target.value,
                                        }))
                                    }
                                >
                                    {SERIES_STATUS.map((status) => (
                                        <option
                                            key={status.value}
                                            value={status.value}
                                        >
                                            {status.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Idiomas */}
                            <div className="form-group">
                                <label className="form-label">
                                    🔊 Idiomas do Áudio
                                </label>
                                <div className="flag-selector">
                                    {LANGUAGES.map((lang) => (
                                        <button
                                            key={lang.code}
                                            type="button"
                                            className={`flag-option ${formData.audioLanguages?.includes(lang.code) ? "selected" : ""}`}
                                            onClick={() =>
                                                toggleLanguage(
                                                    lang.code,
                                                    "audioLanguages",
                                                )
                                            }
                                            title={lang.name}
                                        >
                                            {lang.flag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    💬 Idiomas das Legendas
                                </label>
                                <div className="flag-selector">
                                    {LANGUAGES.map((lang) => (
                                        <button
                                            key={lang.code}
                                            type="button"
                                            className={`flag-option ${formData.subtitleLanguages?.includes(lang.code) ? "selected" : ""}`}
                                            onClick={() =>
                                                toggleLanguage(
                                                    lang.code,
                                                    "subtitleLanguages",
                                                )
                                            }
                                            title={lang.name}
                                        >
                                            {lang.flag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* MediaInfo Paste */}
                            <div className="form-group">
                                <label className="form-label">MediaInfo (colar aqui)</label>
                                <textarea
                                    className="form-input"
                                    rows={4}
                                    placeholder={"Cola aqui o output do MediaInfo para preencher automaticamente os campos abaixo..."}
                                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem" }}
                                    onPaste={(e) => {
                                        setTimeout(() => {
                                            parseMediaInfo(e.target.value);
                                            e.target.value = "";
                                        }, 0);
                                    }}
                                    onChange={() => {}}
                                />
                            </div>

                            {/* Detalhes Técnicos */}
                            <div className="form-group">
                                <label className="form-label">Género</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ex: Aventura, Família, Fantasia"
                                    value={formData.genre || ""}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            genre: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Duração</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Ex: ±19m24s"
                                        value={formData.duration || ""}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                duration: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Formato Contentor</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Ex: .mp4, .mkv"
                                        value={formData.containerFormat || ""}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                containerFormat: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Vídeo</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ex: x264 | 25fps | 760x572 (4:3)"
                                    value={formData.videoInfo || ""}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            videoInfo: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Áudio</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ex: AAC-LC (2.0) 192kbps"
                                    value={formData.audioInfo || ""}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            audioInfo: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Legendas (info)</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ex: N/A (.ass + .srt softsub / hardsub)"
                                    value={formData.subtitleInfo || ""}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            subtitleInfo: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            {/* AI Section */}
                            <div className="ai-section">
                                <h4>
                                    <Sparkles size={18} /> Assistente AI (Groq)
                                </h4>
                                {aiLoading ? (
                                    <div className="ai-loading">
                                        <div className="spinner" />A
                                        processar...
                                    </div>
                                ) : (
                                    <div className="ai-actions">
                                        <button
                                            className="btn btn-secondary btn-small"
                                            onClick={() => fetchAIInfo("all")}
                                        >
                                            <Sparkles size={14} />
                                            Preencher tudo
                                        </button>
                                        <button
                                            className="btn btn-secondary btn-small"
                                            onClick={() =>
                                                fetchAIInfo("synopsis")
                                            }
                                        >
                                            Gerar sinopse
                                        </button>
                                        <button
                                            className="btn btn-secondary btn-small"
                                            onClick={() =>
                                                fetchAIInfo("episodes")
                                            }
                                        >
                                            Info episódios
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Botões */}
                            <div
                                style={{
                                    display: "flex",
                                    gap: "12px",
                                    marginTop: "24px",
                                }}
                            >
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowAddModal(false)}
                                    style={{ flex: 1 }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={saveSeries}
                                    style={{ flex: 1 }}
                                >
                                    <Check size={18} />
                                    {editMode ? "Guardar" : "Adicionar"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Detalhes */}
            {showDetailModal && selectedSeries && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowDetailModal(false)}
                >
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{selectedSeries.title}</h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowDetailModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {/* Poster + Synopsis side by side */}
                            <div className="detail-header-row">
                                {selectedSeries.poster && (
                                    <img
                                        src={selectedSeries.poster}
                                        alt={selectedSeries.title}
                                        className="detail-poster"
                                    />
                                )}
                                <div className="detail-header-info">
                                    {selectedSeries.synopsis && (
                                        <p className="detail-synopsis">
                                            {selectedSeries.synopsis}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Detail Info List */}
                            <div className="detail-list">
                                <div className="detail-list-row">
                                    <span className="detail-label">Tipo</span>
                                    <span className="detail-value">
                                        {selectedSeries.type === "filme" ? "Filme" : "Série"}
                                    </span>
                                </div>
                                {selectedSeries.channel && (
                                    <div className="detail-list-row">
                                        <span className="detail-label">Canal</span>
                                        <span className="detail-value">{selectedSeries.channel}</span>
                                    </div>
                                )}
                                {selectedSeries.status && selectedSeries.status !== "unknown" && (
                                    <div className="detail-list-row">
                                        <span className="detail-label">Estado</span>
                                        <span className="detail-value" style={{
                                            color: SERIES_STATUS.find((s) => s.value === selectedSeries.status)?.color || "var(--text-secondary)",
                                            fontWeight: "500",
                                        }}>
                                            {SERIES_STATUS.find((s) => s.value === selectedSeries.status)?.label || selectedSeries.status}
                                        </span>
                                    </div>
                                )}
                                {selectedSeries.type === "serie" && (
                                    <>
                                        <div className="detail-list-row">
                                            <span className="detail-label">Temporadas</span>
                                            <span className="detail-value">{selectedSeries.totalSeasons || 0}</span>
                                        </div>
                                        <div className="detail-list-row">
                                            <span className="detail-label">Episódios</span>
                                            <span className="detail-value">{selectedSeries.totalEpisodes || 0}</span>
                                        </div>
                                    </>
                                )}
                                <div className="detail-list-row">
                                    <span className="detail-label">Género</span>
                                    <span className="detail-value">{selectedSeries.genre || "N/A"}</span>
                                </div>
                                <div className="detail-list-row">
                                    <span className="detail-label">Duração</span>
                                    <span className="detail-value">{selectedSeries.duration || "N/A"}</span>
                                </div>
                                <div className="detail-list-row">
                                    <span className="detail-label">Formato</span>
                                    <span className="detail-value">{selectedSeries.containerFormat || "N/A"}</span>
                                </div>
                                <div className="detail-list-row">
                                    <span className="detail-label">Vídeo</span>
                                    <span className="detail-value">{selectedSeries.videoInfo || "N/A"}</span>
                                </div>
                                <div className="detail-list-row">
                                    <span className="detail-label">Áudio</span>
                                    <span className="detail-value">{selectedSeries.audioInfo || "N/A"}</span>
                                </div>
                                <div className="detail-list-row">
                                    <span className="detail-label">Idioma</span>
                                    <span className="detail-value">
                                        {selectedSeries.audioLanguages?.length > 0
                                            ? renderFlags(selectedSeries.audioLanguages)
                                            : "N/A"}
                                    </span>
                                </div>
                                <div className="detail-list-row">
                                    <span className="detail-label">Legendas</span>
                                    <span className="detail-value">
                                        {selectedSeries.subtitleInfo
                                            || (selectedSeries.subtitleLanguages?.length > 0
                                                ? renderFlags(selectedSeries.subtitleLanguages)
                                                : "N/A")}
                                    </span>
                                </div>
                                {user && (
                                    <div className="detail-list-row">
                                        <span className="detail-label">Inserir no GTA</span>
                                        <span className="detail-value">
                                            <select
                                                className="form-select"
                                                style={{ fontSize: "0.85rem", padding: "2px 6px", maxWidth: "150px" }}
                                                value={
                                                    selectedSeries.gtaInsert === null || selectedSeries.gtaInsert === undefined
                                                        ? "null"
                                                        : selectedSeries.gtaInsert ? "sim" : "nao"
                                                }
                                                onChange={(e) => handleGtaChange(selectedSeries.id, e.target.value)}
                                            >
                                                <option value="null">Não definido</option>
                                                <option value="sim">Sim</option>
                                                <option value="nao">Não</option>
                                            </select>
                                        </span>
                                    </div>
                                )}
                            </div>

                            {selectedSeries.type === "serie" && (
                                <>
                                    <div
                                        className="card-progress"
                                        style={{ marginBottom: "16px" }}
                                    >
                                        <div
                                            className="progress-bar"
                                            style={{ height: "10px" }}
                                        >
                                            <div
                                                className="progress-fill"
                                                style={{
                                                    width: `${calculateProgress(selectedSeries)}%`,
                                                }}
                                            />
                                        </div>
                                        <span
                                            className="progress-text"
                                            style={{ fontSize: "0.9rem" }}
                                        >
                                            Progresso:{" "}
                                            {calculateProgress(selectedSeries)}%
                                        </span>
                                    </div>

                                    {getMissingEpisodes(selectedSeries).length >
                                        0 && (
                                        <div className="missing-episodes">
                                            <h4>
                                                <AlertCircle size={16} />{" "}
                                                Episódios em falta
                                            </h4>
                                            <p className="missing-list">
                                                {getMissingEpisodes(
                                                    selectedSeries,
                                                )
                                                    .slice(0, 20)
                                                    .join(", ")}
                                                {getMissingEpisodes(
                                                    selectedSeries,
                                                ).length > 20 &&
                                                    ` ... e mais ${getMissingEpisodes(selectedSeries).length - 20}`}
                                            </p>
                                        </div>
                                    )}

                                    {/* Lista de episódios */}
                                    <div className="episodes-section">
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: "12px",
                                            }}
                                        >
                                            <h3 style={{ margin: 0 }}>Episódios</h3>
                                            <div style={{ display: "flex", gap: "8px" }}>
                                                <button
                                                    className="btn btn-secondary btn-small"
                                                    onClick={() => loadSeriesLinks(selectedSeries.id, true)}
                                                    disabled={linksLoading}
                                                >
                                                    {linksLoading ? "A carregar..." : "Atualizar Links"}
                                                </button>
                                            {user && (
                                                <button
                                                    className="btn btn-primary btn-small"
                                                    onClick={() => {
                                                        setEpisodeForm({
                                                            seasonNumber: 1,
                                                            episodeNumber: 1,
                                                            endEpisodeNumber: 1,
                                                            name: "",
                                                            namePattern: "",
                                                            owned: true,
                                                            batchMode: false,
                                                            links: [],
                                                        });
                                                        setShowAddEpisodeModal(true);
                                                    }}
                                                >
                                                    <Plus size={14} />
                                                    Adicionar Episódio
                                                </button>
                                            )}
                                            </div>
                                        </div>
                                        {selectedSeries.seasons?.map(
                                            (season) => (
                                                <div
                                                    key={season.number}
                                                    style={{
                                                        marginBottom: "16px",
                                                    }}
                                                >
                                                    <h4
                                                        style={{
                                                            fontSize: "0.85rem",
                                                            marginBottom: "8px",
                                                            color: "var(--text-secondary)",
                                                        }}
                                                    >
                                                        Temporada{" "}
                                                        {season.number}
                                                    </h4>
                                                    <div
                                                        className="episodes-list"
                                                        style={{
                                                            maxHeight: "200px",
                                                        }}
                                                    >
                                                        {season.episodes?.map(
                                                            (ep) => (
                                                                <div
                                                                    key={ep.number}
                                                                    className="episode-item"
                                                                    style={{ flexWrap: "wrap" }}
                                                                >
                                                                    {ep.owned ? (
                                                                        <Check
                                                                            size={16}
                                                                            style={{ color: "var(--success)", flexShrink: 0 }}
                                                                        />
                                                                    ) : (
                                                                        <X
                                                                            size={16}
                                                                            style={{ color: "var(--accent)", flexShrink: 0 }}
                                                                        />
                                                                    )}
                                                                    <span className="episode-number">
                                                                        T{String(season.number).padStart(2, "0")}{" "}
                                                                        E{String(ep.number).padStart(2, "0")}
                                                                    </span>
                                                                    <span className="episode-name">
                                                                        {ep.name || "Sem nome"}
                                                                    </span>
                                                                    {user && (
                                                                        <div className="episode-actions">
                                                                            <button
                                                                                className="btn btn-icon episode-edit-btn"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openEditEpisode(ep, season.number);
                                                                                }}
                                                                                title="Editar episódio"
                                                                            >
                                                                                <Edit size={14} />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    {ep.links?.length > 0 && (
                                                                        <div className="episode-links-row">
                                                                            {ep.links.map((link) => (
                                                                                <span key={link.id} className="episode-link-badge">
                                                                                    {getServerLogo(link.name) && (
                                                                                        <img
                                                                                            src={getServerLogo(link.name)}
                                                                                            alt=""
                                                                                            style={{ width: 12, height: 12 }}
                                                                                            onError={(e) => { e.target.style.display = "none"; }}
                                                                                        />
                                                                                    )}
                                                                                    <a
                                                                                        href={link.url}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        style={{ color: "var(--accent)", textDecoration: "none" }}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    >
                                                                                        {link.name}
                                                                                    </a>
                                                                                    {user && (
                                                                                        <button
                                                                                            className="link-remove-btn"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                deleteEpisodeLink(link.id);
                                                                                            }}
                                                                                            title="Remover link"
                                                                                        >
                                                                                            <X size={10} />
                                                                                        </button>
                                                                                    )}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                        <div className="server-links-inline">
                                            <div className="server-links-inline-header">
                                                <h4>Links por Servidor</h4>
                                                <div className="server-links-controls">
                                                    <select
                                                        className="form-select"
                                                        style={{ minWidth: "160px" }}
                                                        value={selectedHost}
                                                        onChange={(e) => setSelectedHost(e.target.value)}
                                                        disabled={seriesLinkHosts.length === 0}
                                                    >
                                                        {seriesLinkHosts.length === 0 ? (
                                                            <option value="">Sem servidores</option>
                                                        ) : (
                                                            seriesLinkHosts.map((host) => (
                                                                <option key={host} value={host}>
                                                                    {host} ({seriesLinks[host]?.length || 0})
                                                                </option>
                                                            ))
                                                        )}
                                                    </select>
                                                    <button
                                                        className="btn btn-secondary btn-small"
                                                        onClick={() => loadSeriesLinks(selectedSeries.id, true)}
                                                        disabled={linksLoading}
                                                    >
                                                        {linksLoading ? "A carregar..." : "Recarregar"}
                                                    </button>
                                                    {selectedHost && activeHostLinks.length > 0 && (
                                                        <>
                                                            <button
                                                                className="btn btn-secondary btn-small"
                                                                onClick={() => copyServerUrls(selectedHost, activeHostLinks)}
                                                                title="Copiar só URLs"
                                                            >
                                                                Copiar URLs
                                                            </button>
                                                            <button
                                                                className="btn btn-secondary btn-small"
                                                                onClick={() => copyServerLinks(selectedHost, activeHostLinks)}
                                                                title="Copiar lista completa"
                                                            >
                                                                Copiar Tudo
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {linksLoading ? (
                                                <p className="server-links-empty">A carregar links...</p>
                                            ) : seriesLinkHosts.length === 0 ? (
                                                <p className="server-links-empty">
                                                    Nenhum link adicionado ainda.
                                                </p>
                                            ) : (
                                                <div className="server-links-list">
                                                    {activeHostLinks.map((link) => (
                                                        <div key={link.id} className="server-link-item">
                                                            <span className="server-link-ep">
                                                                T{String(link.seasonNumber).padStart(2, "0")}{" "}
                                                                E{String(link.episodeNumber).padStart(2, "0")}
                                                            </span>
                                                            <span className="server-link-name">
                                                                {link.episodeName || "Sem nome"}
                                                            </span>
                                                            <a
                                                                href={link.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="server-link-url"
                                                            >
                                                                {link.url}
                                                            </a>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {user && (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "12px",
                                        marginTop: "24px",
                                    }}
                                >
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            deleteSeries(selectedSeries.id)
                                        }
                                        style={{ flex: 1 }}
                                    >
                                        <Trash2 size={18} />
                                        Apagar
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                            openEditModal(selectedSeries)
                                        }
                                        style={{ flex: 1 }}
                                    >
                                        <Edit size={18} />
                                        Editar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Definições */}
            {showSettingsModal && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowSettingsModal(false)}
                >
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "500px" }}
                    >
                        <div className="modal-header">
                            <h2>Definições</h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowSettingsModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="settings-group">
                                <h3>🤖 API Groq (AI)</h3>
                                <div className="form-group">
                                    <label className="form-label">
                                        API Key
                                    </label>
                                    <input
                                        type="password"
                                        className="form-input"
                                        placeholder="gsk_..."
                                        value={groqKey}
                                        onChange={(e) =>
                                            setGroqKey(e.target.value)
                                        }
                                    />
                                    <p className="form-hint">
                                        Obtém a tua API key gratuita em{" "}
                                        <a
                                            href="https://console.groq.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "var(--accent)" }}
                                        >
                                            console.groq.com
                                        </a>
                                    </p>
                                </div>
                            </div>

                            <div className="settings-group">
                                <h3>🎬 TMDB API (Capas)</h3>
                                <div className="form-group">
                                    <label className="form-label">
                                        API Key
                                    </label>
                                    <input
                                        type="password"
                                        className="form-input"
                                        placeholder="..."
                                        value={tmdbKey}
                                        onChange={(e) =>
                                            setTmdbKey(e.target.value)
                                        }
                                    />
                                    <p className="form-hint">
                                        Obtém a tua API key gratuita em{" "}
                                        <a
                                            href="https://www.themoviedb.org/settings/api"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "var(--accent)" }}
                                        >
                                            themoviedb.org
                                        </a>
                                    </p>
                                </div>
                            </div>

                            <div className="settings-group">
                                <h3>📊 Dados</h3>
                                <p
                                    style={{
                                        fontSize: "0.85rem",
                                        color: "var(--text-secondary)",
                                        marginBottom: "12px",
                                    }}
                                >
                                    Os teus dados são guardados localmente no
                                    navegador.
                                </p>
                                <div style={{ display: "flex", gap: "12px" }}>
                                    <button
                                        className="btn btn-secondary btn-small"
                                        onClick={() => {
                                            const data = JSON.stringify(
                                                series,
                                                null,
                                                2,
                                            );
                                            const blob = new Blob([data], {
                                                type: "application/json",
                                            });
                                            const url =
                                                URL.createObjectURL(blob);
                                            const a =
                                                document.createElement("a");
                                            a.href = url;
                                            a.download =
                                                "video-tracker-backup.json";
                                            a.click();
                                            showToast("Backup exportado!");
                                        }}
                                    >
                                        Exportar backup
                                    </button>
                                    <label
                                        className="btn btn-secondary btn-small"
                                        style={{ cursor: "pointer" }}
                                    >
                                        Importar backup
                                        <input
                                            type="file"
                                            accept=".json"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const reader =
                                                        new FileReader();
                                                    reader.onload = (event) => {
                                                        try {
                                                            const data =
                                                                JSON.parse(
                                                                    event.target
                                                                        .result,
                                                                );
                                                            setSeries(data);
                                                            showToast(
                                                                "Backup importado!",
                                                            );
                                                        } catch {
                                                            showToast(
                                                                "Ficheiro inválido",
                                                                "error",
                                                            );
                                                        }
                                                    };
                                                    reader.readAsText(file);
                                                }
                                            }}
                                        />
                                    </label>
                                </div>
                            </div>

                            <button
                                className="btn btn-primary"
                                onClick={() => setShowSettingsModal(false)}
                                style={{ width: "100%", marginTop: "16px" }}
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Adicionar Episódio */}
            {showAddEpisodeModal && selectedSeries && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowAddEpisodeModal(false)}
                >
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "600px" }}
                    >
                        <div className="modal-header">
                            <h2>Adicionar Episódio - {selectedSeries.title}</h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowAddEpisodeModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {/* Batch Mode Toggle */}
                            <div className="form-group">
                                <label
                                    className="form-checkbox"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={episodeForm.batchMode}
                                        onChange={(e) =>
                                            setEpisodeForm({
                                                ...episodeForm,
                                                batchMode: e.target.checked,
                                                endEpisodeNumber: e.target.checked
                                                    ? episodeForm.episodeNumber
                                                    : 1,
                                            })
                                        }
                                    />
                                    <span>Adicionar múltiplos episódios</span>
                                </label>
                            </div>

                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">
                                        Temporada *
                                    </label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        min="1"
                                        value={episodeForm.seasonNumber}
                                        onChange={(e) =>
                                            setEpisodeForm({
                                                ...episodeForm,
                                                seasonNumber: parseInt(
                                                    e.target.value,
                                                ),
                                            })
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        {episodeForm.batchMode ? "Episódio Inicial *" : "Número do Episódio *"}
                                    </label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        min="1"
                                        value={episodeForm.episodeNumber}
                                        onChange={(e) =>
                                            setEpisodeForm({
                                                ...episodeForm,
                                                episodeNumber: parseInt(
                                                    e.target.value,
                                                ),
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            {episodeForm.batchMode && (
                                <div className="form-group">
                                    <label className="form-label">
                                        Episódio Final *
                                    </label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        min={episodeForm.episodeNumber}
                                        value={episodeForm.endEpisodeNumber}
                                        onChange={(e) =>
                                            setEpisodeForm({
                                                ...episodeForm,
                                                endEpisodeNumber: parseInt(
                                                    e.target.value,
                                                ),
                                            })
                                        }
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label className="form-label">
                                    {episodeForm.batchMode
                                        ? "Padrão do Nome (use {n} para o número)"
                                        : "Nome do Episódio"}
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder={episodeForm.batchMode
                                        ? "Ex: Episódio {n} ou Capítulo {n}"
                                        : "Ex: O Início"}
                                    value={episodeForm.batchMode ? episodeForm.namePattern : episodeForm.name}
                                    onChange={(e) =>
                                        setEpisodeForm({
                                            ...episodeForm,
                                            [episodeForm.batchMode ? "namePattern" : "name"]: e.target.value,
                                        })
                                    }
                                />
                            </div>

                            {/* Links do episódio */}
                            <div className="form-group">
                                <label className="form-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    Links (opcional)
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-small"
                                        style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                                        onClick={() =>
                                            setEpisodeForm((prev) => ({
                                                ...prev,
                                                links: [...prev.links, { server: "Mega", url: "" }],
                                            }))
                                        }
                                    >
                                        <Plus size={12} /> Servidor
                                    </button>
                                </label>
                                {episodeForm.links.map((link, idx) => (
                                    <div key={idx} style={{ display: "flex", gap: "6px", marginBottom: "6px", alignItems: "center" }}>
                                        <select
                                            className="form-select"
                                            style={{ width: "130px", flexShrink: 0 }}
                                            value={link.server}
                                            onChange={(e) => {
                                                const updated = [...episodeForm.links];
                                                updated[idx] = { ...updated[idx], server: e.target.value };
                                                setEpisodeForm((prev) => ({ ...prev, links: updated }));
                                            }}
                                        >
                                            <option value="Mega">Mega</option>
                                            <option value="1fichier">1fichier</option>
                                            <option value="Google Drive">Google Drive</option>
                                            <option value="Mediafire">Mediafire</option>
                                            <option value="Dropbox">Dropbox</option>
                                            <option value="OneDrive">OneDrive</option>
                                            <option value="Outro">Outro</option>
                                        </select>
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="https://..."
                                            style={{ flex: 1 }}
                                            value={link.url}
                                            onChange={(e) => {
                                                const updated = [...episodeForm.links];
                                                updated[idx] = { ...updated[idx], url: e.target.value };
                                                setEpisodeForm((prev) => ({ ...prev, links: updated }));
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-icon"
                                            style={{ padding: "4px", flexShrink: 0 }}
                                            onClick={() => {
                                                setEpisodeForm((prev) => ({
                                                    ...prev,
                                                    links: prev.links.filter((_, i) => i !== idx),
                                                }));
                                            }}
                                            title="Remover"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="form-group">
                                <label
                                    className="form-checkbox"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={episodeForm.owned}
                                        onChange={(e) =>
                                            setEpisodeForm({
                                                ...episodeForm,
                                                owned: e.target.checked,
                                            })
                                        }
                                    />
                                    <span>Já possuo este episódio</span>
                                </label>
                            </div>

                            {/* Botões */}
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    marginTop: "24px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowAddEpisodeModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => saveEpisode(false)}
                                    style={{ flex: 1 }}
                                >
                                    <Check size={18} />
                                    Guardar
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => saveEpisode(true)}
                                    style={{ flex: 1, background: "var(--success)" }}
                                >
                                    <Plus size={18} />
                                    Guardar e Novo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Editar Episódio */}
            {showEditEpisodeModal && editEpisodeData && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowEditEpisodeModal(false)}
                >
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "600px" }}
                    >
                        <div className="modal-header">
                            <h2>
                                Editar Episódio - T{String(editEpisodeData.seasonNumber).padStart(2, "0")}{" "}
                                E{String(editEpisodeData.number).padStart(2, "0")}
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowEditEpisodeModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label className="form-label">Nome do Episódio</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={editEpisodeData.name}
                                    onChange={(e) =>
                                        setEditEpisodeData((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label
                                    className="form-checkbox"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={editEpisodeData.owned}
                                        onChange={(e) =>
                                            setEditEpisodeData((prev) => ({
                                                ...prev,
                                                owned: e.target.checked,
                                            }))
                                        }
                                    />
                                    <span>Já possuo este episódio</span>
                                </label>
                            </div>

                            <div className="form-group">
                                <label className="form-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    Links (opcional)
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-small"
                                        style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                                        onClick={addEditEpisodeLink}
                                    >
                                        <Plus size={12} /> Servidor
                                    </button>
                                </label>
                                {(editEpisodeData.links || []).map((link, idx) => (
                                    <div key={link.id || idx} style={{ display: "flex", gap: "6px", marginBottom: "6px", alignItems: "center" }}>
                                        <select
                                            className="form-select"
                                            style={{ width: "130px", flexShrink: 0 }}
                                            value={link.server}
                                            onChange={(e) => updateEditEpisodeLink(idx, "server", e.target.value)}
                                        >
                                            <option value="Mega">Mega</option>
                                            <option value="1fichier">1fichier</option>
                                            <option value="Google Drive">Google Drive</option>
                                            <option value="Mediafire">Mediafire</option>
                                            <option value="Dropbox">Dropbox</option>
                                            <option value="OneDrive">OneDrive</option>
                                            <option value="Outro">Outro</option>
                                        </select>
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="https://..."
                                            style={{ flex: 1 }}
                                            value={link.url}
                                            onChange={(e) => updateEditEpisodeLink(idx, "url", e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-icon"
                                            style={{ padding: "4px", flexShrink: 0 }}
                                            onClick={() => removeEditEpisodeLink(idx)}
                                            title="Remover"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    marginTop: "24px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => deleteEpisode(editEpisodeData.id)}
                                >
                                    <Trash2 size={18} />
                                    Apagar
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowEditEpisodeModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={saveEditedEpisode}
                                    style={{ flex: 1 }}
                                >
                                    <Check size={18} />
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Authentication Modal */}
            {showAuthModal && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowAuthModal(false)}
                >
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "400px" }}
                    >
                        <div className="modal-header">
                            <h2>
                                {authMode === "login"
                                    ? "Iniciar Sessão"
                                    : "Criar Utilizador"}
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowAuthModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">

                            <form
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    const { username, email, password } =
                                        authForm;

                                    if (authMode === "register") {
                                        if (
                                            password !==
                                            authForm.confirmPassword
                                        ) {
                                            showToast(
                                                "Passwords não coincidem",
                                                "error",
                                            );
                                            return;
                                        }
                                        await register(
                                            username,
                                            email,
                                            password,
                                        );
                                    } else {
                                        await login(username, password);
                                    }
                                }}
                            >
                                <div className="form-group">
                                    <label className="form-label">
                                        Username
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={authForm.username}
                                        onChange={(e) =>
                                            setAuthForm((prev) => ({
                                                ...prev,
                                                username: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>

                                {authMode === "register" && (
                                    <div className="form-group">
                                        <label className="form-label">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={authForm.email}
                                            onChange={(e) =>
                                                setAuthForm((prev) => ({
                                                    ...prev,
                                                    email: e.target.value,
                                                }))
                                            }
                                            required
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        className="form-input"
                                        value={authForm.password}
                                        onChange={(e) =>
                                            setAuthForm((prev) => ({
                                                ...prev,
                                                password: e.target.value,
                                            }))
                                        }
                                        required
                                        minLength="6"
                                    />
                                </div>

                                {authMode === "register" && (
                                    <div className="form-group">
                                        <label className="form-label">
                                            Confirmar Password
                                        </label>
                                        <input
                                            type="password"
                                            className="form-input"
                                            value={authForm.confirmPassword}
                                            onChange={(e) =>
                                                setAuthForm((prev) => ({
                                                    ...prev,
                                                    confirmPassword:
                                                        e.target.value,
                                                }))
                                            }
                                            required
                                            minLength="6"
                                        />
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ width: "100%", marginTop: "16px" }}
                                >
                                    {authMode === "login"
                                        ? "Entrar"
                                        : "Criar Utilizador"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Screen */}
            {authLoading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "var(--bg-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                >
                    <div style={{ textAlign: "center" }}>
                        <div
                            className="spinner"
                            style={{ margin: "0 auto 16px" }}
                        />
                        <p>A verificar autenticação...</p>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`toast ${toast.type}`}>
                    {toast.type === "success" ? (
                        <Check size={20} style={{ color: "var(--success)" }} />
                    ) : (
                        <AlertCircle
                            size={20}
                            style={{ color: "var(--accent)" }}
                        />
                    )}
                    {toast.message}
                </div>
            )}
        </div>
    );
}

export default App;
